import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { IdentityClientService } from '@auth';
import { ServiceCode } from '@enums';
import { AdminUserNotification, NotificationApplicationParameters } from './admin-user-notification';
import { NotificationType } from './notification-type';

const DEFAULT_DETAILS_PAGE = 'application-details';

@Injectable({
    providedIn: 'root'
})

export class SasNotificationsRouterService {
    private readonly router = inject(Router);
    private readonly identityClientService = inject(IdentityClientService);

    // Module prefix by serviceCode (see modules.routes.ts)
    private readonly moduleByServiceCode: Record<string, string> = {
        [ServiceCode.Planning]: 'planning',
        [ServiceCode.BuildingControl]: 'buildingcontrol',
        [ServiceCode.Enforcement]: 'enforcements',
        [ServiceCode.PreApps]: 'preapps'
    };

    // Officers use the admin details view where the module has one.
    private readonly detailsPageByServiceCode: Record<string, string> = {
        [ServiceCode.Planning]: 'application-details'
    };

    navigateToSource(notification: AdminUserNotification): void {
        const params = notification.typeParameters as NotificationApplicationParameters;

        switch (notification.type) {
            case NotificationType.OfficerReportGenerated:
                this.routeToApplication(params);
                break;
            // Future notification types can be handled here, e.g. routeToApplication(params, 'responses').
            default:
                if (params?.applicationId != null) {
                    this.routeToApplication(params);
                }
                else {
                    console.warn(`Unknown notification type: ${notification.type}`);
                }
        }
    }
// Destination depends on where the user currently is: inside the Built Environment embedded
// area it stays there (embedded sign-requests view); anywhere else it goes to the standalone screen.
navigateToPendingSignatures(): void {
    const clientUrl = this.identityClientService.get()?.url ?? '';
    const currentUrl = this.router.url;

    const target = currentUrl.includes('/buildingcontrol/embedded/')
        ? `/${clientUrl}/buildingcontrol/embedded/signatures/sign-requests`
        : `/${clientUrl}/planning/signature-requests`;

    this.router.navigateByUrl(target);
}

navigateToPendingComments(commentType: 'consultee' | 'citizen'): void {
    const clientUrl = this.identityClientService.get()?.url ?? '';
    this.router.navigateByUrl(`/${clientUrl}/planning/comments-pending/${commentType}`);
}

    private routeToApplication({ applicationId, serviceCode }: NotificationApplicationParameters, tab?: string): void {
        const modulePath = serviceCode ? this.moduleByServiceCode[serviceCode] : undefined;
        if (!serviceCode || !modulePath || applicationId == null) {
            console.warn(`Cannot route notification: serviceCode=${serviceCode}, applicationId=${applicationId}`);
            return;
        }

        const detailsPage = this.detailsPageByServiceCode[serviceCode] ?? DEFAULT_DETAILS_PAGE;

        // Routes carry the client prefix (see app.routes.ts).
        const clientUrl = this.identityClientService.get()?.url ?? '';

        this.router.navigate([`/${clientUrl}/${modulePath}/${detailsPage}`, applicationId, ...(tab ? [tab] : [])]);
    }
}
